import type { BuildingBounds } from "building-types/building/buildingBounds";
import type { Building, GenerateBuildingFeatureSet } from "building-types/building/buildingTypes";
import type { Doc } from "building-types/doc";
import type { ElectricalSystemViewTable } from "building-types/electrical/electricalViewTypes";
import type { IsolatableEntityHandle } from "building-types/handles/docEntityHandleTypes";
import type { LevelPerimeters } from "building-types/levels/levelPerimeters";
import type { OptionChoices } from "building-types/options/optionChoices";
import { optionSetEntityRelationsDefault } from "building-types/options/optionSetEntityRelationsTypes";
import type { ParameterValue } from "building-types/options/parameter";
import type { PlanarGraphLevelAssociations } from "building-types/planarGraphs/planarGraphAssociationTypes";
import type { PlanarGraphPerimeterInfo } from "building-types/planarGraphs/planarGraphPerimeterInfo";
import type { RoomEdgeAssociations } from "building-types/rooms/roomEdgeAssociations";
import type { WallPlaneMasses } from "building-types/walls2/wallPlane";
import type { WallMassSystem, WallSystem } from "building-types/walls/wallSytemTypes";
import type { Table } from "common/data/table";
import objectMap from "common/utils/objectMap";
import { defaultExteriorPalette } from "doc/settings/defaultExteriorMaterials";
import { defaultHomeSettings } from "doc/settings/defaultSettings";
import { defaultInteriorPalette } from "doc/settings/interiorMaterialSettingsLibrary";
import { polygonPlanarGraphNew } from "geom/polygonPlanarGraph";
import { RoomIndexImpl } from "../elements/rooms/roomIndexImpl";
import { WallIndexImpl } from "../elements/walls/wallIndexImpl";
import { generateLevelsForWorlds, selectLevels } from "../generation/generateLevels";

const emptyObject = Object.freeze({});

const defaultBuildingBounds: BuildingBounds = {
  highestRoofPoint: 0,
  buildingBottomElevation: 0,
  centroidX: 0,
  centroidY: 0,
  centroidZ: 0,
  width: 0,
  depth: 0,
  height: 0,
  aabb: {
    minX: 0,
    minY: 0,
    minZ: 0,
    maxX: 0,
    maxY: 0,
    maxZ: 0,
  },
};

const defaultWallPlaneMasses: WallPlaneMasses = {
  miters: {},
  masses: [],
  massGrids: {},
};

const defaultRoomEdgeAssociations: RoomEdgeAssociations = {
  wallCuts: [],
  labels: [],
  wallWidths: [],
  wallAssemblies: [],
  controllers: [],
  loads: [],
  porchColumnPatterns: [],
  foundationPatterns: [],
  dimensionBehavior: [],
};

const emptyBuilding: Omit<Building, "doc"> = {
  blockPerimeters: emptyObject,
  rooms: emptyObject,
  roomLevelIndices: emptyObject,
  roomAnnotation: emptyObject,
  roomsByLevel: emptyObject,
  cornerBoards: emptyObject,
  wainscoting: emptyObject,
  roofSystems: emptyObject,
  roofVolumes: emptyObject,
  roofSystemLevels: emptyObject,
  roofSystemClipContexts: emptyObject,
  roofSoffits: emptyObject,
  driveways: emptyObject,
  props: emptyObject,
  porches: emptyObject,
  floorPlates: emptyObject,
  floorSystems: emptyObject,
  floorBoundaries: emptyObject,
  horizontalAssemblyFloors: emptyObject,
  horizontalAssemblyFloorSystems: emptyObject,
  horizontalAssemblyFoundations: emptyObject,
  horizontalAssemblyFoundationSystems: emptyObject,
  wallPlanes: emptyObject,
  wallPlaneOpeningDomainOperations: emptyObject,
  wallPlaneTrims: emptyObject,
  interiorWallPlaneTrimMoldings: emptyObject,
  wallMasses: emptyObject,
  walls: emptyObject,
  wallFramingSystems: emptyObject,
  wallFramingRegionPickFaces: emptyObject,
  gableEndWallFramingSystems: emptyObject,
  planarGraphs: emptyObject,
  groupByEntityType: emptyObject,
  planarGraphPerimeters: emptyObject,
  planarGraphLevelAssociations: emptyObject,
  doorPlacements: emptyObject,
  solidWallOverrides: emptyObject,
  doorUnits: emptyObject,
  drawingRefs: emptyObject,
  sites: emptyObject,
  siteShapes: emptyObject,
  sheets: emptyObject,
  windowPlacements: emptyObject,
  windowUnits: emptyObject,
  casework: emptyObject,
  labels: emptyObject,
  wallCuts: emptyObject,
  wallPaths: emptyObject,
  wallAssemblyOverrides: emptyObject,
  wallLayers: emptyObject,
  totalFloorArea: 0,
  elevationNoteEdgeBehaviors: emptyObject,
  wallAssemblyEdgeBehaviors: emptyObject,
  baseTrimMoldings: emptyObject,
  crownTrimMoldings: emptyObject,
  ceilingPatchTable: emptyObject,
  openFullDoors: emptyObject,
  modifiers: emptyObject,
  modifiersByInstanceChain: emptyObject,
  columns: emptyObject,
  docHash: "",
  completedStages: Object.freeze([]),
  mirror: false,
  mirrorTranslationX: 0,
  mirrorTranslationXOverride: undefined,
  mirrorInvert: undefined,
  referenceGeom: emptyObject,
  optionSetEntityRelations: optionSetEntityRelationsDefault(),
  settings: defaultHomeSettings,
  enabledFeatures: {
    roofFraming: false,
    horizontalAssemblies: false,
    modelComponents: false,
  },
  name: "",
  optionChoices: emptyObject,
  globalParamValues: emptyObject,
  defaultExteriorCamera: undefined,
  groups: emptyObject,
  entityGraphNodes: [],
  entityGraphRelationships: [],
  entityDisabledByGroup: emptyObject,
  measurements: emptyObject,
  levelsAttributes: emptyObject,
  settingsSets: emptyObject,
  exteriorPalette: defaultExteriorPalette({ sortIndex: 0 }),
  interiorPalette: defaultInteriorPalette({ sortIndex: 0 }),
  blocks: emptyObject,
  roomIndex: new RoomIndexImpl(emptyObject, emptyObject, emptyObject, emptyObject),
  buildingGraph: {
    volumeNodes: emptyObject,
    volumeEdges: [],
    faceNodes: emptyObject,
    faceNodesToEdgeIds: emptyObject,
    faceEdges: [],
    faceTopologies: emptyObject,
    buildingPlanes: emptyObject,
  },
  wallPlaneMasses: defaultWallPlaneMasses,
  wallPlaneFilterMatches: emptyObject,
  roomFloorAssemblies: emptyObject,
  roomFloorFinishes: emptyObject,
  roomClassFloorAssemblyGroups: emptyObject,
  ceilings: emptyObject,
  ceilingsByLevel: emptyObject,
  dimensionEdgeBehaviors: emptyObject,
  porchColumnEdgeBehaviors: emptyObject,
  foundationAssemblyEdgeBehaviors: emptyObject,
  foundationPatternEdgeBehaviors: emptyObject,
  labelEdgeBehaviors: emptyObject,
  trimEdgeBehaviors: emptyObject,
  levelAnnotation: emptyObject,
  annotation: emptyObject,
  modelHostedDrawings: emptyObject,
  elevationDrawingAnnotation: emptyObject,
  sectionViewAnnotation: emptyObject,
  roomEdgeAssociations: defaultRoomEdgeAssociations,
  dormers: emptyObject,
  skylights: emptyObject,
  skylightUnits: emptyObject,
  foundations: emptyObject,
  foundationHorizontalAssemblies: emptyObject,
  foundationPaths: emptyObject,
  foundationHorizontalAssembliesPaths: emptyObject,
  floorPaths: emptyObject,
  foundationSuperSystems: emptyObject,
  openingPlacements: emptyObject,
  windowInfills: emptyObject,
  windowShutters: emptyObject,
  levelExtrusions: emptyObject,
  roomOutlineTable: emptyObject,
  elevationNotes: emptyObject,
  porchColumnPatterns: emptyObject,
  foundationPatterns: emptyObject,
  foundationAssemblyOverrides: emptyObject,
  dimensionBehavior: emptyObject,
  stairSystems: emptyObject,
  railings: emptyObject,
  floorAreaTakeoffs: emptyObject,
  buildingBounds: defaultBuildingBounds,
  facades: emptyObject,
  facadeSweepStrata: emptyObject,
  facadeSpanStrata: emptyObject,
  wallSweeps: emptyObject,
  wallStructuralDomains: emptyObject,
  roomLayoutElectrical: emptyObject,
  levels: emptyObject,
  groundLevel: undefined,
  electrical: [],
  electricalRulesByRoom: emptyObject,
  electricalControllerCandidates: emptyObject,
  electricalSystemView: emptyObject,
  issues: [],
  evaluatedOptionSets: emptyObject,
  resolvedDatums: emptyObject,
  exteriorDimensions: emptyObject,
  foundationExteriorDimensions: emptyObject,
  foundationInteriorDimensions: emptyObject,
  selectorIdToEntityHighlightHandle: emptyObject,
  interiorDimensions: emptyObject,
  dimensions: emptyObject,
  dimensionsByEntityHandleHash: emptyObject,
  analyticFoundationEntities: emptyObject,
  analyticColumnEntities: emptyObject,
  analyticPorchEntities: emptyObject,
  analyticFoundationSuperSystemEntities: emptyObject,
  analyticFoundationHorizontalAssemblyEntities: emptyObject,
  analyticWallEntities: emptyObject,
  analyticRoofEntities: emptyObject,
  analyticCeilingEntities: emptyObject,
  analyticRoofSoffitEntities: emptyObject,
  analyticDoorTrimEntities: emptyObject,
  analyticWindowTrimEntities: emptyObject,
  analyticInteriorTrimEntities: emptyObject,
  analyticRailingEntities: emptyObject,
  analyticWallStructuralDomainEntities: emptyObject,
  analyticFloorStructureEntities: emptyObject,
  analyticExteriorFinishEdgeEntities: emptyObject,
  roofFraming: emptyObject,
  lumberRails: emptyObject,
  lumberRailToMemberInfo: emptyObject,
  openingToResolvedOpeningFraming: emptyObject,
  facadeSpanStratumAssociations: emptyObject,
  facadeSweepStratumAssociations: emptyObject,
  datumSpanAssociations: emptyObject,
  datumAbstractions: emptyObject,
  idToParents: {},
  componentToTransforms: {},
  relatedRoomsMap: {},
  relatedStairSystemsMap: {},
  framedFloorAssemblies: emptyObject,
  slabFloorAssemblies: emptyObject,
};

const defaultEnabledFeatures: GenerateBuildingFeatureSet = {
  roofFraming: false,
  horizontalAssemblies: false,
  modelComponents: false,
};

export function buildingNew(
  name: string,
  optionChoices: OptionChoices,
  globalParamValues: Table<ParameterValue>,
  doc: Doc,
  enabledFeatures: GenerateBuildingFeatureSet = defaultEnabledFeatures,
  isolatedEntity?: IsolatableEntityHandle,
  mirrorTranslationXOverride?: number,
  mirrorInvert?: boolean,
): Building {
  return {
    ...emptyBuilding,
    name,
    optionChoices,
    globalParamValues,
    doc,
    // the issues is list is immutable
    issues: Object.freeze([]),
    enabledFeatures,
    isolatedEntity,
    mirrorTranslationXOverride,
    mirrorInvert,
  };
}

/**
 * Generates a minimal Building suitable for satisfying the
 * interface, but without most contents.
 */
export function generateDefaultBuilding(
  name: string,
  optionChoices: OptionChoices,
  doc: Doc,
  enabledFeatures: GenerateBuildingFeatureSet = {
    roofFraming: false,
    horizontalAssemblies: false,
    modelComponents: false,
  },
): Building {
  const building = buildingNew(name, optionChoices, {}, doc, enabledFeatures);

  const levelInfo = generateLevelsForWorlds(selectLevels(building as Building));

  const emptyPlanarGraph = polygonPlanarGraphNew([]);
  const emptyWallIndex = new WallIndexImpl({}, [], {}, emptyPlanarGraph);
  const emptyWallMassSystem: WallMassSystem = {
    wallIndex: emptyWallIndex,
    walls: {},
    halfEdgeToWall: [],
    openConnectedRoomSets: [],
  };
  const emptyWallSystem: WallSystem = {
    wallIndex: emptyWallIndex,
    walls: {},
    halfEdgeToWall: [],
    openConnectedRoomSets: [],
    entityType: "WALL_SYSTEM",
    pickHandle: undefined,
    id: "",
    docEntityHandle: undefined,
    halfStoryWallCrossSectionFaces: [],
  };
  const emptyElectricalSystemViewTable: ElectricalSystemViewTable = {
    loads: [],
    links: [],
    controllers: [],
    controllerCandidates: [],
  };
  const emptyPlanarGraphPerimeterInfo: PlanarGraphPerimeterInfo = {
    allPerimeterFaces: [],
    allPerimeters: { faceVertices: [], faceHalfEdges: [] },
    allPerimeterStreetOrientations: {},

    uncoveredPerimeterFaces: [],
    uncoveredPerimeters: { faceVertices: [], faceHalfEdges: [] },
    uncoveredPerimeterStreetOrientations: {},

    coveredPerimeterFaces: [],
    coveredPerimeters: { faceVertices: [], faceHalfEdges: [] },
    coveredPerimeterStreetOrientations: {},

    insideCornerHalfEdges: [],
    unsupportedPerimeterVertices: [],
  };
  const emptyPlanarGraphLevelAssociations: PlanarGraphLevelAssociations = {
    halfEdgesBelow: [],
    halfEdgesAbove: [],
    facesBelow: [],
    facesAbove: [],
  };
  const emptyLevelPerimeters: LevelPerimeters = {
    perimeters: { faceVertices: [], faceHalfEdges: [] },
    perimeterPolyLine2s: [],
    perimeterPolyLine2sWithoutExteriorFaces: [],
    allPerimeterPolyLine2s: [],
  };

  const onEachLevel = <T>(v: T) => {
    return objectMap(levelInfo.levels, () => v);
  };

  return {
    ...building,
    ...levelInfo,
    ceilingsByLevel: onEachLevel([]),
    roomsByLevel: onEachLevel([]),
    planarGraphs: onEachLevel(emptyPlanarGraph),
    planarGraphPerimeters: onEachLevel(emptyPlanarGraphPerimeterInfo),
    planarGraphLevelAssociations: onEachLevel(emptyPlanarGraphLevelAssociations),
    wallMasses: onEachLevel(emptyWallMassSystem),
    walls: onEachLevel(emptyWallSystem),
    electricalSystemView: onEachLevel(emptyElectricalSystemViewTable),
    labels: onEachLevel(emptyObject),
    dimensionBehavior: onEachLevel(emptyObject),
    foundationPatterns: onEachLevel(emptyObject),
    porchColumnPatterns: onEachLevel(emptyObject),
    wallAssemblyOverrides: onEachLevel(emptyObject),
    foundationAssemblyOverrides: onEachLevel(emptyObject),
    wallCuts: onEachLevel(emptyObject),
    floorPlates: onEachLevel(emptyLevelPerimeters),
  };
}
